import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";

import LoadingIndicator from "../../../../loading-indicator";
import TeaserNews, {
  teaserNewsPropTypes,
} from "../../../../teaser-base/news/teaser-news";
import TeaserPerson, {
  teaserPersonPropTypes,
} from "../../../../teaser-base/person/teaser-person";
import { pagerFullPagesAction } from "../../../../../app-actions";
import { teaserEventPropTypes } from "../../../../teaser-base/event/teaser-event";
import TeaserGeneral, {
  teaserGeneralPropTypes,
} from "../../../../teaser-base/general/teaser-general";
import { teaserProjectPropTypes } from "../../../../teaser-base/project/teaser-project";
import useTeaserQuery from "./use-teaser-query";
import TeaserParticipantSwitch from "../../../../teaser-base/teaser-participant-switch";
import ActsCarousel from "../acts/acts-carousel";
import ActsCarouselSimple from "../acts/acts-carousel-simple";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import UniversalSliderButton from "@default/general-components/universal-slider-button";

const ComponentTeaserListCarousel = (props) => {
  const dispatch = useDispatch();
  const { simpleDesign } = useAccessibilityContext();
  const sliderRef = useRef();

  const { nodes, loading } = useTeaserQuery(props.manualNodes, {
      ...props,
      limit: 15,
    }),
    [slideIndex, setSlideIndex] = useState(0);

  const handleBeforeChange = (oldIndex, newIndex) => {
    setSlideIndex(newIndex);
  };

  const sliderSettingsStandard = {
    arrows: true,
    centerMode: false,
    dots: false,
    infinite: false,
    fade: !simpleDesign,
    slidesToShow: simpleDesign ? 2 : 1,
    slidesToScroll: 1,
    touchMove: true,
    beforeChange: handleBeforeChange,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: simpleDesign ? 2.5 : 1.25,
        },
      },
      {
        breakpoint: 786,
        settings: {
          slidesToShow: simpleDesign ? 1 : 1.66,
        },
      },
      {
        breakpoint: 550,
        settings: {
          slidesToShow: simpleDesign ? 1 : 1.25,
          touchMove: true,
        },
      },
    ],
  };

  const sliderSettingsPress = {
    adaptiveHeight: false,
    arrows: simpleDesign,
    fade: false,
    dots: false,
    slidesToShow: 2.75,
    slidesToScroll: 1,
    touchMove: true,
    centerMode: false,
    infinite: false,
    beforeChange: handleBeforeChange,
    responsive: [
      {
        breakpoint: 786,
        settings: {
          slidesToShow: 2.33,
          touchMove: true,
        },
      },
      {
        breakpoint: 550,
        settings: {
          slidesToShow: 1,
          touchMove: true,
        },
      },
    ],
  };

  const sliderSettings =
    props.newsType && props.newsType === "press"
      ? sliderSettingsPress
      : sliderSettingsStandard;

  const pushPagerFullPageConfig = () => {
    if (props.pagerFullPage && nodes) {
      const pagerFullPagesConfig = {
        id: props.id,
        items:
          props.mode === "manual"
            ? props.manualNodes.map((item) => item.entity)
            : nodes.items,
        overviewLink: props.pagerFullPageOverviewLink,
      };

      dispatch(pagerFullPagesAction(pagerFullPagesConfig));
    }
  };

  useEffect(() => {
    pushPagerFullPageConfig();
  }, [nodes]);

  if (loading) {
    return <LoadingIndicator />;
  }

  const filteredNodes = nodes.filter(Boolean);
  const isPrevDisabled = slideIndex === 0;
  const isNextDisabled =
    slideIndex >=
    Math.floor(filteredNodes.length - sliderSettings?.slidesToShow);
  const showButtons = props.newsType === "press";

  if (filteredNodes.length >= 4 && props.type !== "participant") {
    return (
      <div className={simpleDesign ? "container no-padding" : "slider-wrapper"}>
        {showButtons && (
          <UniversalSliderButton
            type="prev"
            onClick={() => sliderRef.current?.slickPrev()}
            sliderClassNames={isPrevDisabled ? "slick-disabled" : ""}
          />
        )}
        <Slider {...sliderSettings} ref={sliderRef}>
          {filteredNodes.map((item, index) => (
            <React.Fragment key={index}>
              {(() => {
                switch (item.entityBundle) {
                  case "news":
                    return (
                      <TeaserNews
                        item={item}
                        pagerFullPage={props.pagerFullPage}
                        active={slideIndex === index}
                        activeBefore={
                          slideIndex !== 0 && slideIndex - 1 === index
                        }
                      />
                    );
                  case "person":
                    return (
                      <TeaserPerson
                        item={item}
                        pagerFullPage={props.pagerFullPage}
                        activeBefore={
                          slideIndex !== 0 && slideIndex - 1 === index
                        }
                      />
                    );
                  case "landingpage":
                    return (
                      <TeaserGeneral
                        item={item}
                        pagerFullPage={props.pagerFullPage}
                        activeBefore={
                          slideIndex !== 0 && slideIndex - 1 === index
                        }
                      />
                    );
                  case "participant":
                    return (
                      <TeaserParticipantSwitch
                        item={item}
                        pagerFullPage={props.pagerFullPage}
                      />
                    );
                  default:
                    return null;
                }
              })()}
            </React.Fragment>
          ))}
        </Slider>
        {showButtons && (
          <UniversalSliderButton
            type="next"
            onClick={() => sliderRef.current?.slickNext()}
            sliderClassNames={isNextDisabled ? "slick-disabled" : ""}
          />
        )}
      </div>
    );
  }

  if (
    filteredNodes.length >= 4 &&
    (props.participantType === "act" ||
      props.participantType === "speaker" ||
      props.participantType === "moderator")
  ) {
    return (
      <>
        {simpleDesign ? (
          <ActsCarouselSimple nodes={nodes} />
        ) : (
          <ActsCarousel nodes={nodes} />
        )}
      </>
    );
  }
};

ComponentTeaserListCarousel.propTypes = {
  limit: PropTypes.number.isRequired,
  manualNodes: PropTypes.arrayOf(
    PropTypes.oneOfType([
      teaserNewsPropTypes,
      teaserEventPropTypes,
      teaserPersonPropTypes,
      teaserGeneralPropTypes,
      teaserProjectPropTypes,
    ])
  ),
  id: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["auto", "manual"]),
  type: PropTypes.oneOf(["news", "person", "event", "all", "acts", "speakers"]),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    })
  ),
  pagerFullPageOverviewLink: PropTypes.object,
  pagerFullPage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    .isRequired,
};

export default ComponentTeaserListCarousel;
